import Link from 'next/link';

import { useMemo } from 'react';

import classNames from 'classnames';

import { RewardPool, RewardPoolLeaderboardUser } from '@shared/api/types/rewardPools';
import { nextProjectUrl } from '@shared/constants/urls';
import { getDistributionPolicyFlags } from '@shared/entities/rewardPool';
import { formatPercent } from '@shared/helpers/number';
import { concatCurrencyToAmount } from '@shared/helpers/web3/formatters';

import { UserCard } from '@store:web/components/common/UserCard/UserCard';
import { isRewardPoolCompleted } from '@store:web/components/entities/rewardPool';
import { Table, TableColumn } from '@ui/uikit/components/common/Table';
import { Karma } from '@ui/uikit/components/icons/mono/Karma';
import { Trophy } from '@ui/uikit/components/icons/mono/Trophy';
import { formatReward } from '@ui/uikit/utils';

import { RewardList } from './RewardList';
import { TicketsList } from './TicketsList';

type Props = {
  rewardPool: RewardPool;
  data: RewardPoolLeaderboardUser[];
  userCellLabel: string;
  isLoading?: boolean;
  totalPoints: number;
};

type RewardPoolLeaderboardTableColumn = RewardPoolLeaderboardUser & {
  distribution: string;
};

const leaderBoardToTableDataMapper = (
  data: RewardPoolLeaderboardUser[],
  totalPoints: number,
): RewardPoolLeaderboardTableColumn[] => {
  return data.map((d) => {
    return {
      ...d,
      distribution: formatPercent((d.points || 0) / totalPoints || 0),
    };
  });
};

export const LeaderboardTable = ({
  rewardPool,
  data,
  userCellLabel,
  isLoading,
  totalPoints,
}: Props) => {
  const { isProRata, isRaffleV2, isProRataSwap, isProRataKarma, isRaffleSwap } =
    getDistributionPolicyFlags(rewardPool.distribution_policy);

  const preparedData = useMemo(
    () => leaderBoardToTableDataMapper(data, totalPoints),
    [data, totalPoints],
  );

  const columns: TableColumn<RewardPoolLeaderboardTableColumn>[] = [
    {
      label: userCellLabel,
      name: 'name',
      render: ({ row }) => {
        return (
          <Link target="_blank" href={nextProjectUrl.store(`/user/${row.name}`)}>
            <div className="flex gap-4 py-1">
              {Boolean(row.rank) && (
                <div
                  className={classNames('badge badge-md min-w-12', {
                    'chip-orange': row.rank === 1,
                    'chip-blue': row.rank === 2,
                    'chip-red': row.rank === 3,
                  })}
                >
                  {row.rank}
                </div>
              )}
              <UserCard
                name={row.displayedName || row.name}
                membershipTierId={row.membership}
                src={row.avatar}
                karma={row?.karma}
              />
            </div>
          </Link>
        );
      },
    },
    {
      label: 'Reward',
      name: 'reward',
      hidden: (!isRaffleV2 && !isRaffleSwap) || !isRewardPoolCompleted(rewardPool),
      render: ({ row }) => {
        return <RewardList user={row} rewardPool={rewardPool} />;
      },
    },
    {
      label: 'Winning Ticket',
      name: 'entries',
      hidden: (!isRaffleV2 && !isRaffleSwap) || !isRewardPoolCompleted(rewardPool),
      render: ({ row }) => {
        return <TicketsList user={row} />;
      },
    },
    {
      label: 'Swap Volume',
      name: 'points',
      hidden: !isProRataSwap,
      render: ({ row }) => {
        return concatCurrencyToAmount(formatReward(row.points ?? 0), '$');
      },
    },
    {
      label: 'Karma Volume',
      name: 'points',
      hidden: !isProRataKarma,
      render: ({ row }) => {
        return (
          row.points && (
            <span className="flex items-center gap-1.5">
              <Karma className="text-base-text-pink size-4" />
              <span className="text-caption-sm-a">{formatReward(row.points, 0, 0)}</span>
            </span>
          )
        );
      },
    },
    {
      label: 'Distribution',
      name: 'distribution',
      hidden: !isProRata,
      render: ({ row }) => {
        return (
          <div className="flex gap-1 items-center">
            <Trophy className="icon-xs text-base-text-orange" />
            <div>{row.distribution}</div>
          </div>
        );
      },
    },
  ];

  return <Table columns={columns} data={preparedData} isLoading={isLoading} />;
};
